/**
 * Cookie read/write helpers: `getItem` and the `useCookie` hook. Neither has an importer in this
 * repo.
 */
import { useState } from "react";

/**
 * Reads one cookie by name, URI-decoded. Returns `""` when the cookie isn't set. Without a
 * `document` (the static-export prerender) it returns `JSON.stringify("")`, the two-character
 * string `""`, so `useCookie`'s `JSON.parse` reads it as an empty string.
 */
export const getItem = (key: string): string => {
	if (typeof document !== `undefined`) {
		return document?.cookie.split("; ").reduce((total, currentCookie) => {
			const item = currentCookie.split("=");
			const storedKey = item[0];
			const storedValue = item[1];

			return key === storedKey ? decodeURIComponent(storedValue) : total;
		}, "");
	} else {
		return JSON.stringify("");
	}
};

/** Writes a site-wide (`path=/`) cookie that expires `numberOfDays` days from now. */
const setItem = (key: string, value: any, numberOfDays: number) => {
	const currentDateTime = new Date();
	currentDateTime.setTime(currentDateTime.getTime() + numberOfDays * 60 * 60 * 24 * 1000);

	// Can't use document?.cookies because it is at left hand side of assignment operator
	if (typeof document !== `undefined`) {
		document.cookie = `${key}=${value}; expires=${currentDateTime.toUTCString()}; path=/`;
	}
};

/**
 * A cookie as React state. The value is stored JSON-encoded; `defaultValue` applies when the cookie
 * isn't set. Returns `[value, update]`, where `update(value, numberOfDays)` sets the state and
 * rewrites the cookie. The state is seeded from the cookie on the first render; the cookie is
 * re-read and parsed on every render, but later values are ignored.
 */
const useCookie = (key: string, defaultValue: any) => {
	const getCookie = () => JSON.parse(getItem(key) || JSON.stringify(defaultValue));
	const [cookie, setCookie] = useState(getCookie());

	const updateCookie = (value: any, numberOfDays: number) => {
		setCookie(value);
		setItem(key, JSON.stringify(value), numberOfDays);
	};

	return [cookie, updateCookie];
};

export default useCookie;
